import React, { ChangeEvent, useEffect, useRef } from 'react';
import * as styles from './DocumentViewer.module.scss';
import { Document } from 'types';
import { FormControl, FormLabel, Select, Heading } from 'components/core';
import { useDocumentViewerContext } from 'contexts/DocumentViewerProvider';

export type DocumentViewerProps = {
  documents: Document[];
  selectedId?: number;
  onSelect?: (id: number) => void;
};

export const DocumentViewer = ({
  documents = [],
  selectedId,
  onSelect,
}: DocumentViewerProps) => {
  const {
    containerRef,
    setSelectedDocument,
    selectedDocument,
  } = useDocumentViewerContext();

  const selectedDocumentRef = useRef(selectedDocument);
  selectedDocumentRef.current = selectedDocument;

  useEffect(() => {
    const currentId = selectedDocumentRef.current?.id;
    // Keep the current document when the list refreshes, as urls can change
    const idToShow = selectedId ?? currentId;
    const nextDocument =
      documents.find((d) => d.id === idToShow) ?? documents[0];

    if (nextDocument?.id !== currentId) {
      setSelectedDocument(nextDocument);
    }
  }, [selectedId, documents]);

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedDocId = Number(event.currentTarget.value);
    setSelectedDocument(
      documents.find((d) => d.id === selectedDocId) || documents[0]
    );
    onSelect?.(selectedDocId);
  };

  if (!selectedDocument) return null;

  return (
    <div className={styles.wrapper}>
      {documents.length === 1 && (
        <Heading type="h3">{selectedDocument.label}</Heading>
      )}
      {documents.length > 1 && (
        <FormControl className={styles.selectWrapper}>
          <FormLabel isVisuallyHidden>Switch document</FormLabel>
          <Select
            value={selectedDocument.id ?? undefined}
            onChange={handleChange}
          >
            {documents.map((document) => (
              <option key={document.id} value={document.id ?? undefined}>
                {document.label}
              </option>
            ))}
          </Select>
        </FormControl>
      )}

      <div
        className={styles.wrapper}
        role="document"
        ref={containerRef}
        aria-label={`Preview of: ${selectedDocument.label}`}
      />
    </div>
  );
};
